import { api } from "@/lib/api";
import { formatDate, formatShortDate } from "@/lib/utils";
import type { AdminSubscriptionDetail } from "@/types";
import {
  Badge,
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CreditCard, Package } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const STATUS_COLORS: Record<string, string> = {
  active: "green",
  trialing: "blue",
  past_due: "amber",
  canceled: "red",
  incomplete: "zinc",
  incomplete_expired: "zinc",
  unpaid: "orange",
  paused: "zinc",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Past Due",
  canceled: "Canceled",
  incomplete: "Incomplete",
  incomplete_expired: "Incomplete Expired",
  unpaid: "Unpaid",
  paused: "Paused",
};

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <div className="mb-4 flex items-center gap-2">
        <span className="text-zinc-950 dark:text-white">{icon}</span>
        <span className="text-base font-semibold text-zinc-950 dark:text-white">{title}</span>
      </div>
      {children}
    </div>
  );
}

function formatAmount(amount: number | undefined, currency: string | undefined): string {
  if (amount == null) return "—";
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency ?? "usd").toUpperCase(),
    minimumFractionDigits: 2,
  });
  return formatter.format(amount / 100);
}

export function SubscriptionDetailPage() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery<AdminSubscriptionDetail>({
    queryKey: ["subscription-detail", subscriptionId],
    queryFn: () => api.getSubscriptionDetail(subscriptionId!),
    enabled: !!subscriptionId,
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-4 py-2">
        <Button plain onClick={() => navigate("/admin/subscriptions")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Subscriptions
        </Button>
        <div className="mt-8 text-center">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <Text className="mt-2 text-gray-500 dark:text-zinc-400">Subscription not found</Text>
        </div>
      </div>
    );
  }

  const sub = data;

  return (
    <div className="px-4 py-2">
      {/* Header */}
      <div className="mb-8">
        <Button plain onClick={() => navigate("/admin/subscriptions")} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Subscriptions
        </Button>
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
            <CreditCard className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <Text className="text-2xl font-bold text-zinc-950 dark:text-white">
              {sub.orgName || sub.customerEmail || sub.id}
            </Text>
            <Text className="text-sm font-mono text-zinc-400 dark:text-zinc-500">{sub.id}</Text>
          </div>
          <div className="ml-auto flex gap-2">
            <Badge color={sub.stripeEnv === "sandbox" ? "amber" : "green"}>
              {sub.stripeEnv === "sandbox" ? "Test" : "Prod"}
            </Badge>
            <Badge color={STATUS_COLORS[sub.status] ?? "zinc"}>
              {STATUS_LABELS[sub.status] ?? sub.status.toUpperCase()}
            </Badge>
            {(sub.cancelAtPeriodEnd || sub.cancelAt) && (
              <Badge color="orange">
                Cancels {formatShortDate(sub.cancelAt ?? sub.currentPeriodEnd)}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Info */}
      <Section title="Subscription Details" icon={<CreditCard className="h-5 w-5" />}>
        <DescriptionList>
          <DescriptionTerm>Subscription ID</DescriptionTerm>
          <DescriptionDetails>
            <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
              {sub.id}
            </code>
          </DescriptionDetails>

          <DescriptionTerm>Status</DescriptionTerm>
          <DescriptionDetails>
            <Badge color={STATUS_COLORS[sub.status] ?? "zinc"}>
              {STATUS_LABELS[sub.status] ?? sub.status}
            </Badge>
          </DescriptionDetails>

          <DescriptionTerm>Environment</DescriptionTerm>
          <DescriptionDetails>
            <Badge color={sub.stripeEnv === "sandbox" ? "amber" : "green"}>
              {sub.stripeEnv === "sandbox" ? "Test" : "Production"}
            </Badge>
          </DescriptionDetails>

          <DescriptionTerm>Customer ID</DescriptionTerm>
          <DescriptionDetails>
            <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
              {sub.customerId}
            </code>
          </DescriptionDetails>

          {sub.customerEmail && (
            <>
              <DescriptionTerm>Customer Email</DescriptionTerm>
              <DescriptionDetails>{sub.customerEmail}</DescriptionDetails>
            </>
          )}

          {sub.orgId && (
            <>
              <DescriptionTerm>Organization ID</DescriptionTerm>
              <DescriptionDetails>
                <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
                  {sub.orgId}
                </code>
              </DescriptionDetails>
            </>
          )}

          {sub.orgName && (
            <>
              <DescriptionTerm>Organization</DescriptionTerm>
              <DescriptionDetails>{sub.orgName}</DescriptionDetails>
            </>
          )}

          {sub.planId && (
            <>
              <DescriptionTerm>Plan</DescriptionTerm>
              <DescriptionDetails>
                <Badge color="zinc">{sub.planId.toUpperCase()}</Badge>
              </DescriptionDetails>
            </>
          )}

          <DescriptionTerm>Current Period</DescriptionTerm>
          <DescriptionDetails>
            {formatDate(sub.currentPeriodStart)} – {formatDate(sub.currentPeriodEnd)}
          </DescriptionDetails>

          <DescriptionTerm>Cancel at Period End</DescriptionTerm>
          <DescriptionDetails>
            {sub.cancelAtPeriodEnd ? (
              <Badge color="orange">Yes</Badge>
            ) : (
              <Text className="text-zinc-400">No</Text>
            )}
          </DescriptionDetails>

          {sub.cancelAt && (
            <>
              <DescriptionTerm>Cancels On</DescriptionTerm>
              <DescriptionDetails>
                <Badge color="orange">{formatDate(sub.cancelAt)}</Badge>
              </DescriptionDetails>
            </>
          )}

          {sub.trialStart && (
            <>
              <DescriptionTerm>Trial Start</DescriptionTerm>
              <DescriptionDetails>{formatDate(sub.trialStart)}</DescriptionDetails>
            </>
          )}

          {sub.trialEnd && (
            <>
              <DescriptionTerm>Trial End</DescriptionTerm>
              <DescriptionDetails>{formatDate(sub.trialEnd)}</DescriptionDetails>
            </>
          )}

          {sub.canceledAt && (
            <>
              <DescriptionTerm>Canceled At</DescriptionTerm>
              <DescriptionDetails>{formatDate(sub.canceledAt)}</DescriptionDetails>
            </>
          )}

          <DescriptionTerm>Created</DescriptionTerm>
          <DescriptionDetails>{formatDate(sub.createdAt)}</DescriptionDetails>

          {sub.latestInvoiceId && (
            <>
              <DescriptionTerm>Latest Invoice</DescriptionTerm>
              <DescriptionDetails>
                <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
                  {sub.latestInvoiceId}
                </code>
              </DescriptionDetails>
            </>
          )}

          {sub.defaultPaymentMethodId && (
            <>
              <DescriptionTerm>Default Payment Method</DescriptionTerm>
              <DescriptionDetails>
                <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
                  {sub.defaultPaymentMethodId}
                </code>
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      </Section>

      {/* Subscription Items */}
      {sub.items && sub.items.length > 0 && (
        <div className="mt-8">
          <Section title="Subscription Items" icon={<Package className="h-5 w-5" />}>
            <Table className="[--gutter:--spacing(6)] sm:[--gutter:--spacing(8)]">
              <TableHead>
                <TableRow>
                  <TableHeader>Price ID</TableHeader>
                  <TableHeader>Product ID</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Interval</TableHeader>
                  <TableHeader>Quantity</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sub.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-xs">
                        {item.priceId}
                      </code>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-xs">
                        {item.productId}
                      </code>
                    </TableCell>
                    <TableCell>{formatAmount(item.unitAmount, item.currency)}</TableCell>
                    <TableCell className="capitalize">
                      {item.interval
                        ? `${item.intervalCount && item.intervalCount > 1 ? `Every ${item.intervalCount} ` : ""}${item.interval}`
                        : "—"}
                    </TableCell>
                    <TableCell>{item.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </div>
      )}

      {/* Metadata */}
      {sub.metadata && Object.keys(sub.metadata).length > 0 && (
        <div className="mt-8">
          <Section title="Metadata" icon={<Package className="h-5 w-5" />}>
            <DescriptionList>
              {Object.entries(sub.metadata).map(([key, value]) => (
                <span key={key}>
                  <DescriptionTerm>{key}</DescriptionTerm>
                  <DescriptionDetails>
                    <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
                      {String(value)}
                    </code>
                  </DescriptionDetails>
                </span>
              ))}
            </DescriptionList>
          </Section>
        </div>
      )}
    </div>
  );
}
